import { useRef, useState, KeyboardEvent, ChangeEvent } from 'react';
import { MicButton } from './MicButton';
import { uploadImage, UploadResult } from '../api/uploads';
import { SkillChipRow } from './SkillChipRow';

const API_BASE = import.meta.env.VITE_API_BASE as string;

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

function PaperclipIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7l8.5-8.5a3.5 3.5 0 0 1 5 5L10 19a2 2 0 0 1-2.8-2.8L15 8.4"
      />
    </svg>
  );
}

function Spinner(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
      className="mic-spin"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeDasharray="14 28"
      />
    </svg>
  );
}

export function InputBar({
  onSend,
  disabled,
  placeholder,
  apiKeyConfigured = true,
  enabledSkills = [],
}: {
  onSend: (text: string, attachment?: UploadResult) => void;
  disabled: boolean;
  placeholder?: string;
  apiKeyConfigured?: boolean;
  /** Stage 28: active persona's enabled_skills — drives the SkillChipRow. */
  enabledSkills?: string[];
}) {
  const [value, setValue] = useState('');
  const [attachment, setAttachment] = useState<UploadResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const submit = (): void => {
    const trimmed = value.trim();
    if ((!trimmed && !attachment) || disabled) return;
    onSend(trimmed, attachment ?? undefined);
    setValue('');
    setAttachment(null);
    setErr(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Ctrl+Enter or Cmd+Enter → send
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      submit();
    }
  };

  const appendTranscript = (text: string): void => {
    setValue((prev) => (prev ? `${prev} ${text}` : text));
  };

  const onFilePicked = async (
    e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const result = await uploadImage(file);
      setAttachment(result);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const ph =
    placeholder ??
    (disabled
      ? 'Add your Gemini API key in Settings to begin.'
      : attachment
        ? 'Add a caption or question (optional); Ctrl+Enter to send…'
        : 'Type a message…  (Ctrl+Enter to send)');

  return (
    <div className="input-bar-wrap">
      {attachment ? (
        <div className="input-bar-attachment" role="group" aria-label="Attached image">
          <img
            src={absolute(attachment.signedUrl)}
            alt="Attached"
            className="input-bar-attachment-thumb"
          />
          <span className="muted small input-bar-attachment-meta">
            {attachment.mimeType} · {(attachment.sizeBytes / 1024).toFixed(0)} KB
          </span>
          <button
            type="button"
            className="input-bar-attachment-remove"
            onClick={() => setAttachment(null)}
            aria-label="Remove attachment"
            title="Remove attachment"
          >
            ×
          </button>
        </div>
      ) : null}

      {err ? <div className="status-missing small">Upload: {err}</div> : null}

      {/* Stage 28: skill chip row — only renders when the active persona
          has enabled_skills AND at least one of them matches an uploaded
          skill. Click inserts `/Skill `; double-click submits `/Skill`. */}
      <SkillChipRow
        enabledSkills={enabledSkills}
        value={value}
        onInsert={(next, caret) => {
          setValue(next);
          setTimeout(() => {
            const el = textareaRef.current;
            if (el) {
              el.focus();
              el.setSelectionRange(caret, caret);
            }
          }, 0);
        }}
        onInvokeNow={(slash) => {
          if (!disabled) {
            onSend(slash);
            setValue('');
            setAttachment(null);
            setErr(null);
          }
        }}
      />

      <div className="input-bar">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={(e) => void onFilePicked(e)}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className={
            'input-bar-paperclip' + (attachment ? ' has-attachment' : '')
          }
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading || attachment !== null}
          aria-label="Attach image"
          title="Attach image (≤20MB; PNG / JPEG / WebP / GIF)"
        >
          {uploading ? <Spinner /> : <PaperclipIcon />}
        </button>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={ph}
          disabled={disabled}
          rows={3}
        />
        <MicButton
          onTranscript={appendTranscript}
          disabled={!apiKeyConfigured}
        />
        <button
          className="btn-primary input-send"
          onClick={submit}
          disabled={disabled || (!value.trim() && !attachment)}
        >
          Send
        </button>
      </div>
    </div>
  );
}
